import { useCallback, useEffect, useRef, useState } from "react";

import { delayCall } from "api/util";

/**
 * The one open menu per group, so opening a second can close the first.
 *
 * Module-level rather than context, because the members aren't in one subtree — they are
 * siblings in `DesktopNav`, and a provider would mean a wrapper component whose only job
 * is to hold a value every member already reaches through this hook.
 *
 * Keyed by the caller's group string, holding a stable per-instance object. Nothing is
 * ever iterated: only one entry per group exists at a time, and it is deleted on close and
 * on unmount.
 */
const openByGroup = new Map();

/**
 * Hover-open / delayed-close state for dropdowns and popovers.
 *
 * Opens immediately on pointer enter and cancels any pending close, so moving
 * the pointer back into the trigger keeps the menu open. Closes `delay` ms
 * after the pointer leaves.
 *
 * **Pass a `group` to make menus mutually exclusive.** Without one, each menu's close is
 * on its own timer, so sliding the pointer between hover triggers leaves both panels open
 * for the length of the delay. Opening a grouped menu closes the group's current one
 * **synchronously**, cancelling its pending timer rather than waiting it out. This is the reference's behaviour too: its
 * `openCatMenu` sets `brandMenuOpen: false, acctOpen: false`, and `openBrandMenu` the
 * mirror image.
 *
 * Every open path goes through `onMouseEnter`, including the click/tap toggles that call
 * it directly, so grouping covers those as well.
 *
 * @param {number} [delay]     Close delay in ms. Default 200.
 * @param {Function} [onClose] Extra state to reset when the close fires (e.g.
 *                             clearing a hovered submenu). Held in a ref, so an
 *                             inline arrow is fine.
 * @param {string} [group]     Mutual-exclusion group. Menus sharing a string can't be open
 *                             at once. Omit for a standalone menu.
 * @returns {{ open: boolean, onMouseEnter: Function, onMouseLeave: Function, close: Function }}
 */
export default function useHoverIntent(delay = 200, onClose, group) {
  const [open, setOpen] = useState(false);
  const cancelPendingClose = useRef(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  // Stable identity for this instance in the registry. A plain object whose `close` is
  // refreshed each render, rather than registering the callback itself — that way the
  // "is this entry mine?" comparison can't be fooled by a re-created function.
  const memberRef = useRef(null);
  if (memberRef.current === null) memberRef.current = { close: null };

  const clearPending = useCallback(() => {
    if (cancelPendingClose.current) {
      cancelPendingClose.current();
      cancelPendingClose.current = null;
    }
  }, []);

  const onMouseEnter = useCallback(() => {
    clearPending();

    if (group) {
      const current = openByGroup.get(group);
      // Close the incumbent before opening, so the two panels never paint together.
      if (current && current !== memberRef.current) current.close?.();
      openByGroup.set(group, memberRef.current);
    }

    setOpen(true);
  }, [clearPending, group]);

  const close = useCallback(() => {
    clearPending();
    if (group && openByGroup.get(group) === memberRef.current) {
      openByGroup.delete(group);
    }
    setOpen(false);
    onCloseRef.current?.();
  }, [clearPending, group]);

  memberRef.current.close = close;

  // The delayed close runs the same `close` as everything else, so it deregisters from the
  // group too. Leaving a closed menu in the registry would make the next open in that
  // group call `close` on it a second time, re-firing its `onClose`. Safe to route through
  // `close` even though it calls `clearPending` on the very timer that invoked it —
  // `delayCall`'s canceller is a `clearTimeout`, a no-op once the timer has fired.
  const onMouseLeave = useCallback(() => {
    clearPending();
    cancelPendingClose.current = delayCall(close, delay);
  }, [clearPending, close, delay]);

  // Don't leave a timer running, or a registry entry behind, after unmount.
  useEffect(() => {
    const member = memberRef.current;

    return () => {
      clearPending();
      if (group && openByGroup.get(group) === member) openByGroup.delete(group);
    };
  }, [clearPending, group]);

  return { open, onMouseEnter, onMouseLeave, close };
}
